import { cloneDeep } from "lodash";
import { getDBFormattedDate } from "./dateServices";
import { truncate } from "./stringServices";
import { mathFix } from "./mathServices";
import { computeDebitCredit } from "../../../backend/src/common/accountingLine/accountingLineServices";
import { isInsideAccountingClosing } from "../services/accountingPeriods";

import getEnv from "../env";

const env = getEnv();

export function getPurchaseTypes() {
    return [
        "PURCHASE_INVOICE",
        "CURRENT_ACCOUNT",
        "OPERATOR_ACCOUNT",
        "EXPENSE_REPORT",
        "EXPENSE_REPORT_DOCUMENT",
        "WITHOUT_INVOICE",
    ];
}
export function getSalesTypes() {
    return ["SALES_INVOICE", "ZTILL_RECEIPT", "SALES_INVOICE_AUTO"];
}

export const defaultDocumentFilter = {
    search: "",
    type: "ALL",
    status: "ALL",
    filterByPrice: false,
    company: {
        name: "",
        siret: "",
        siren: "",
        vatNumber: "",
    },
    // providerId: undefined,
    tags: [],
    amount: {
        maximum: "",
        minimum: "",
    },
    date: {
        begin: "",
        end: "",
        format: "YYYY-MM-DD",
    },
    pagination: {
        sortBy: ["date"],
        descending: [true],
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
    },
};

export function getAmountSign(document) {
    let sign = -1;
    if (getSalesTypes().indexOf(document.type) !== -1) {
        if (document.creditNote) sign = 1;
    } else if (!document.creditNote) {
        sign = 1;
    }
    return sign;
}
export function updateAmountSigned(document) {
    const sign = getAmountSign(document);

    document.netAmountSigned = sign * document.netAmount;
    document.vatAmountSigned = sign * document.vatAmount;
    document.grossAmountSigned = -sign * document.grossAmount;
}

export function getDocumentTitle(document, truncateText: boolean = true) {
    let name = "-";
    if (document) {
        if (document.thirdPartyCompany && document.thirdPartyCompany.name) {
            name = document.thirdPartyCompany.name;
            if (document.displayName) {
                name += " - " + document.displayName;
            }
        } else if (document.displayName) {
            name = document.displayName;
        } else {
            name = document.name;
        }
    }
    return truncateText ? truncate(name) : name;
}

export function getEmptyDocument(
    companyId,
    name,
    type,
    date,
    ownerId,
    thirdPartyCompanyId = null,
    accountingLines = null,
    accountingLinesLocked = false,
) {
    if (accountingLines?.length) {
        let ALs = cloneDeep(accountingLines);
        ALs = ALs.filter((al) => mathFix(al.amountSigned, 2) !== 0);

        if (!ALs.length) {
            console.error("No accounting lines with amount different from 0");
            return null;
        } else if (ALs.length === 1) {
            console.error("getEmptyDocument: Only 1 accounting lines");
            debugger;
            return null;
        } else if (ALs.some((al) => !al.accountableAccount?.connect?.uniqueName)) {
            console.error(
                "getEmptyDocument: Some accounting lines have no accountable account : please use 'getConnectAccountableAccount()'",
            );
            debugger;
            return null;
        } else {
            const sum = mathFix(
                ALs.reduce((acc, al) => acc + al.amountSigned, 0),
                2,
            );
            if (sum) {
                console.error("getEmptyDocument: Unbalanced accounting lines : ", sum);
                debugger;
                return null;
            }
        }

        const document: any = {
            name: name,
            date: getDBFormattedDate(date),
            type: type,
            status: "VALIDATED",
            company: { connect: { id: companyId } },
            useForReconciliation: false,
            show: true,
        };

        if (ownerId) {
            document.owner = { connect: { id: ownerId } };
        }
        if (thirdPartyCompanyId) {
            document.thirdPartyCompany = { connect: { id: thirdPartyCompanyId } };
        }

        for (const al of ALs) {
            al.date = getDBFormattedDate(al.date);
            computeDebitCredit(al);
        }

        document.accountingLines = { create: ALs };
        document.displayName = ALs[0].wording;

        if (accountingLinesLocked) {
            document.accountingLinesLocked = accountingLinesLocked;
        }
        return document;
    }
    console.error("getEmptyDocument: No accounting lines");
    debugger;
    return null;
}

export function containsValidatedReconciliation(document: any) {
    if (!document.accountingLines || !document.accountingLines.length) {
        return false;
    }
    for (const al of document.accountingLines) {
        if (al.reconciliation && al.reconciliation.status === "VALIDATED") {
            return true;
        }
    }
    return false;
}

export function getDocumentReconciliations(document: any) {
    const reconciliations = [];
    if (!document.accountingLines || !document.accountingLines.length) {
        return reconciliations;
    }
    for (const al of document.accountingLines) {
        if (al.reconciliation) {
            reconciliations.push(al.reconciliation);
        }
    }
    return reconciliations;
}

export function getMissingtReconciliations(document: any) {
    const als = [];
    if (!document.accountingLines || !document.accountingLines.length) {
        return als;
    }
    for (const al of document.accountingLines) {
        if (al.useForReconciliation && !al.reconciliation) {
            als.push(al);
        }
    }
    return als;
}

export function getReconciliationStatus(document: any, selectedCompany, vueComponent) {
    if (document.type === "CURRENT_ACCOUNT") return "compte courant";
    if (document.type === "OPERATOR_ACCOUNT") return "compte de l'exploitant";
    if (document.type === "ZTILL_RECEIPT") return "Z de caisse";
    const reconciliations = getDocumentReconciliations(document);
    const missingReconciliationAls = getMissingtReconciliations(document);
    if (!reconciliations.length) {
        if (missingReconciliationAls.length) {
            if (selectedCompany.solution !== "MANAGEMENT") {
                return vueComponent.$t("document.statusDescription." + document.status);
            }
            return "Non réglée";
        }
        return "Pas de règlement nécessaire";
    }

    const edition = !!reconciliations.find((r) => r.status === "EDITION");
    const validated = !!reconciliations.find((r) => r.status === "VALIDATED");

    const done = !!reconciliations.find((r) => r.status === "PENDING_VALIDATION_FROM_CLIENT");
    const pendingValidation = !!reconciliations.find((r) => r.status === "PENDING_VALIDATION");

    if (done || pendingValidation) {
        if (missingReconciliationAls.length) {
            return "Partiellement réglée";
        }
        return "Règlement à vérifier";
    }
    if (edition || validated) {
        if (missingReconciliationAls.length) {
            return "Partiellement réglée";
        }
        return "Réglée";
    }
    return "Non réglée";
}

export function getReconciliationCallToAction(document: any, selectedCompany, isAccountingUser) {
    if (document.type === "CURRENT_ACCOUNT") return "Voir le compte courant";
    if (document.type === "OPERATOR_ACCOUNT") return "Voir le compte de l'exploitant";
    if (document.type === "ZTILL_RECEIPT") return "Voir la caisse";

    const reconciliations = getDocumentReconciliations(document);
    const missingReconciliationAls = getMissingtReconciliations(document);
    if (!reconciliations.length) {
        if (
            missingReconciliationAls.length &&
            (selectedCompany.solution === "MANAGEMENT" || isAccountingUser)
        ) {
            return "Trouver le paiement";
        }
        return "";
    }

    const edition = !!reconciliations.find((r) => r.status === "EDITION");
    const validated = !!reconciliations.find((r) => r.status === "VALIDATED");

    const done = !!reconciliations.find((r) => r.status === "PENDING_VALIDATION_FROM_CLIENT");
    const pendingValidation = !!reconciliations.find((r) => r.status === "PENDING_VALIDATION");

    if (edition || done || pendingValidation) {
        return "Vérifier";
    }
    if (validated) {
        return "Afficher";
    }

    debugger;
    return "";
}

export function downloadFileDocument({
    documentId,
    original,
    workingCopy = false,
}: {
    documentId: string;
    original: boolean;
    workingCopy?: boolean;
}) {
    if (original) {
        if (!workingCopy) {
            window.open(
                `${env.VUE_APP_BACKEND}/document/download/${documentId}/original`,
                "_blank",
            );
        } else {
            window.open(
                `${env.VUE_APP_BACKEND}/document/download/${documentId}/workingCopy`,
                "_blank",
            );
        }
    } else {
        window.open(`${env.VUE_APP_BACKEND}/document/download/${documentId}`, "_blank");
    }
}

export function adjustmentsPurchasesConditions(al) {
    return (
        al.accountableAccount?.name &&
        (al.accountableAccount.name.startsWith("60") ||
            al.accountableAccount.name.startsWith("61") ||
            al.accountableAccount.name.startsWith("62") ||
            al.accountableAccount.name.startsWith("63") ||
            al.accountableAccount.name.startsWith("64") ||
            al.accountableAccount.name.startsWith("65") ||
            al.accountableAccount.name.startsWith("66") ||
            al.accountableAccount.name.startsWith("67"))
    );
}

export function adjustmentsSalesConditions(al) {
    return (
        al.accountableAccount?.name &&
        (al.accountableAccount.name.startsWith("70") ||
            al.accountableAccount.name.startsWith("71") ||
            al.accountableAccount.name.startsWith("72") ||
            al.accountableAccount.name.startsWith("74") ||
            al.accountableAccount.name.startsWith("75") ||
            al.accountableAccount.name.startsWith("76") ||
            al.accountableAccount.name.startsWith("77"))
    );
}

export function isDeletionDisabled(document, isAccountingUser, store) {
    if (!document) return true;

    if (document.invoice || document.accountingClosing || document.isResultOf?.id) {
        return true;
    }

    if (document.date && isInsideAccountingClosing(store, document.date)) {
        return true;
    }

    if (
        document.type === "BANK_CHARGE" ||
        document.type === "WITHOUT_INVOICE" ||
        document.type === "WITH_DELTA" ||
        document.type === "WAITING_CASHING"
    ) {
        if (!isAccountingUser && containsValidatedReconciliation(document)) {
            return true;
        }
    }

    if (
        document.type === "MISCELLANEOUS" ||
        document.type === "BANK_STATEMENT" ||
        document.type === "UNKNOWN" ||
        document.type === "SEPA" ||
        document.type === "SUBSCRIPTION" ||
        document.type === "SUBSCRIPTION_ACCOUNTING" ||
        document.type === "LOAN" ||
        document.type === "LOAN_ACCOUNTING" ||
        document.type === "LEASING" ||
        document.type === "LEASING_ACCOUNTING"
    ) {
        return false;
    }

    if (document.type === "EXPENSE_REPORT_DOCUMENT" && document.expenseReport) {
        return true;
    }

    if (document.type === "VAT" && document.isVatDocumentOf) {
        return true;
    }

    if (document.type === "ZTILL_RECEIPT" && document.isDocOfZTillReceipt) {
        return true;
    }

    if (
        document.type !== "SALES_INVOICE" &&
        document.type !== "PURCHASE_INVOICE" &&
        document.type !== "PAY_SLIP" &&
        document.type !== "MISCELLANEOUS_OPERATION" &&
        document.type !== "CARRY_FORWARD" &&
        document.type !== "CURRENT_ACCOUNT"
    ) {
        return true;
    }

    return !!document.vatDeclaration;
}
